<script setup lang="ts">
import { useCmsSection } from "@shopware/composables";
import type { CmsSectionSidebar } from "@shopware/composables";
import { getTranslatedProperty } from "@shopware/helpers";
import { useCategory, useCategoryListing } from "#imports";
import { computed } from "vue";
import type { Schemas, operations } from "#shopware";

const router = useRouter();
const route = useRoute();

const props = defineProps<{
  content: CmsSectionSidebar;
}>();
const { getPositionContent } = useCmsSection(props.content);

const sidebarBlocks = getPositionContent("sidebar");
const mainBlocks = getPositionContent("main");

const {
  getTotal,
  getCurrentSortingOrder,
  changeCurrentSortingOrder,
  getSortingOrders,
} = useCategoryListing();
const hideMobileFilter = ref(false);
const hideMobileSort = ref(false);

function showSortOptions() {
  hideMobileFilter.value = false;
  hideMobileSort.value = true;
}

function showFilterOptions() {
  hideMobileSort.value = false;
  hideMobileFilter.value = true;
}

const currentSortingOrder = computed({
  get: (): string => getCurrentSortingOrder.value || "",
  set: async (order: string): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        order,
      },
    });

    await changeCurrentSortingOrder(order, {
      ...(route.query as unknown as operations["searchPage post /search"]["body"]),
      limit: route.query.limit ? Number(route.query.limit) : 15,
    });
  },
});
</script>

<template>
  <div class="container max-w-[90rem]">
    <div class="flex justify-center md:justify-between mb-5">
      <h3 class="font-bold text-2xl xl:min-w-[274px]">
        {{ $t("product.loaded.weHave") }}
        <strong class="text-skin-theme">{{ getTotal }}</strong>
        {{ $t("product.loaded.shown") }}
      </h3>
      <div
        @click="hideMobileSort = false"
        class="sidebar_overlay bg-skin-primary w-full h-full opacity-30 fixed top-0 left-0 z-50 md:hidden"
        :class="{ 'mobile:hidden': !hideMobileSort }"
      ></div>
      <div
        class="filter_sort mobile:w-full mobile:fixed bottom-0 left-0 mobile:bg-skin-white mobile:p-5 [&.up]:bottom-0 transition-all duration-300"
        :class="{
          'mobile:translate-y-0 z-[500]': hideMobileSort,
          'mobile:translate-y-full z-0': !hideMobileSort,
        }"
      >
        <select
          class="hidden md:inline-block min-w-[181px]"
          name="sort-option"
          @change="currentSortingOrder = $event.target.value"
        >
          <template v-for="sorting in getSortingOrders" :key="sorting.key">
            <option
              :value="sorting.key"
              :selected="sorting.key === getCurrentSortingOrder"
            >
              {{ sorting.label }}
            </option>
          </template>
        </select>
        <ul class="block md:hidden">
          <li
            v-for="sorting in getSortingOrders"
            :key="sorting.key"
            class="relative py-2"
          >
            <input
              type="radio"
              name="sort"
              :value="sorting.key"
              v-model="currentSortingOrder"
              class="absolute top-0 left-0 z-10 w-full h-full opacity-0 [&:checked~span]:bg-skin-theme"
              @click="currentSortingOrder = sorting.key"
            />
            <label for="">{{ sorting.label }}</label>
            <span
              class="inline-block w-4 h-4 rounded-full absolute top-2 right-0 border border-skin-theme text-center text-skin-white text-xs"
              ><i class="fa-solid fa-check"></i
            ></span>
          </li>
        </ul>
      </div>
    </div>

    <div class="flex">
      <div
        class="filter_sidebar relative pl-0 w-[30rem] mobile:max-w-[80%] md:w-[15rem] lg:w-[17.50rem] shop_sidebar prose-h4:font-bold prose-h4:text-base prose-h4:mb-4 mt-5 mobile:fixed mobile:h-full mobile:bg-skin-white top-0 left-0 mobile:m-0 mobile:pt-14 transition-all duration-300 [&.open]:left-0"
        :class="{
          'mobile:translate-x-0 z-[500]': hideMobileFilter,
          'mobile:-translate-x-full z-0': !hideMobileFilter,
        }"
      >
        <button
          type="button"
          class="close_sidebar w-10 h-10 md:hidden absolute top-4 -right-10 bg-skin-body rounded-r-lg bg-humburger-close bg-no-repeat bg-[center] bg-[length:1rem]"
          :class="{ 'hidden': !hideMobileFilter }"
          @click="hideMobileFilter = false"
        ></button>
        <h4 class="w-full px-5 py-3 absolute md:hidden top-0 left-0">
          {{ $t("product.filter.title") }}
        </h4>

        <div
          class="mobile:max-h-[calc(100vh_-_8rem)] mobile:overflow-auto mobile:px-5 mt-2"
        >
          <CmsGenericBlock
            v-for="cmsBlock in sidebarBlocks"
            :key="cmsBlock.id"
            :content="cmsBlock"
          />
        </div>

        <div
          class="filter_action grid bg-skin-body absolute w-full bottom-0 left-0 px-5 py-2.5 gap-x-4 uppercase grid-cols-2 md:hidden [&>button]:!pt-2 [&>button]:!pb-1"
        >
          <button type="button" class="btn px-2" @click="hideMobileFilter = false">
            {{ $t("product.filter.hide") }}
          </button>
          <button
            type="button"
            class="btn-outline btn-sort px-2 text-center"
            @click="showSortOptions"
          >
            {{ $t("product.filter.sort") }}
          </button>
        </div>
      </div>
      <div
        @click="hideMobileFilter = false"
        class="sidebar_overlay bg-skin-primary w-full h-full opacity-30 fixed top-0 left-0 z-50 md:hidden"
        :class="{ 'mobile:hidden': !hideMobileFilter }"
      ></div>

      <CmsGenericBlock
        v-for="cmsBlock in mainBlocks"
        :key="cmsBlock.id"
        class="w-full"
        :content="cmsBlock"
      />
    </div>
    <div
      class="sidebar_overlay hidden bg-skin-primary w-full h-full opacity-30 fixed top-0 left-0 z-50 md:!hidden"
    ></div>

    <div
      class="filter_bar md:hidden w-full text-center p-2.5 shadow-[0px_-1px_15px_-2px_rgba(0,0,0,0.3)] fixed bottom-0 left-0 bg-skin-white z-40 transition-all duration-300 [&.down]:-bottom-20"
    >
      <button
        type="button"
        class="btn btn-filter m-auto flex items-center gap-1"
        @click="showFilterOptions"
      >
      <span class="-mt-1"><img src="assets/icons/icon_filter.svg" alt="icon-filter" /></span>
        {{ $t("product.filter.title") }}
      </button>
    </div>
  </div>
</template>
